import { useTranslation } from "react-i18next";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Download } from "lucide-react";
import { getInstructionsTextToExport } from "../lib/exportInstructions";
import { ListIcon, XIcon } from "@phosphor-icons/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";
import { useSearchParams } from "react-router-dom";

export function InstructionsHeader() {
  const { t } = useTranslation();
  const { isLoading } = useProjectStore();
  const { selectedProject } = useProjectStore();
  const { isOpenChapterSidebar, toggleChapterSidebar, setViewMode } = useChapterStore();
  const { isSidebarDocked } = useWorkspaceLayout();
  const [, setSearchParams] = useSearchParams();

  const handleClose = () => {
    setViewMode("chapter");
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("view");
        return params;
      },
      { replace: true }
    );
  };

  const handleExport = () => {
    if (!selectedProject) return;
    const textToExport = getInstructionsTextToExport({
      projectInstructions: selectedProject.projectInstructions ?? "",
      chaptersInstructions:
        selectedProject.projectChapters?.reduce((acc, chapter) => {
          acc[chapter.projectTemplateChapter.name] = chapter.chapterInstructions ?? "";
          return acc;
        }, {} as Record<string, string>) ?? {}
    });
    const blob = new Blob([textToExport], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedProject.name} - Instructions.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="project-header">
      <div className="bg-white h-14 min-h-14 px-4 py-2 flex justify-between items-center border-b border-innoveraZinc-100">
        <div className="flex items-center gap-4 justify-between w-full">
          <div className="text-[14px] flex items-center gap-2">
            {!isSidebarDocked && (
              <AppButton
                data-testid="instructions-header-sidebar-toggle"
                appearance="text"
                size="icon"
                startAdornment={isOpenChapterSidebar ? <XIcon /> : <ListIcon />}
                data-chapter-sidebar-toggle
                onClick={() => toggleChapterSidebar(!isOpenChapterSidebar)}
              />
            )}
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M24.125 9.75H18.5C18.0149 9.75 17.5364 9.86295 17.1025 10.0799C16.6685 10.2969 16.2911 10.6119 16 11C15.7089 10.6119 15.3315 10.2969 14.8975 10.0799C14.4636 9.86295 13.9851 9.75 13.5 9.75H7.875C7.70924 9.75 7.55027 9.81585 7.43306 9.93306C7.31585 10.0503 7.25 10.2092 7.25 10.375V21.625C7.25 21.7908 7.31585 21.9497 7.43306 22.0669C7.55027 22.1842 7.70924 22.25 7.875 22.25H13.5C13.9973 22.25 14.4742 22.4475 14.8258 22.7992C15.1775 23.1508 15.375 23.6277 15.375 24.125C15.375 24.2908 15.4408 24.4497 15.5581 24.5669C15.6753 24.6842 15.8342 24.75 16 24.75C16.1658 24.75 16.3247 24.6842 16.4419 24.5669C16.5592 24.4497 16.625 24.2908 16.625 24.125C16.625 23.6277 16.8225 23.1508 17.1742 22.7992C17.5258 22.4475 18.0027 22.25 18.5 22.25H24.125C24.2908 22.25 24.4497 22.1842 24.5669 22.0669C24.6842 21.9497 24.75 21.7908 24.75 21.625V10.375C24.75 10.2092 24.6842 10.0503 24.5669 9.93306C24.4497 9.81585 24.2908 9.75 24.125 9.75ZM13.5 21H8.5V11H13.5C13.9973 11 14.4742 11.1975 14.8258 11.5492C15.1775 11.9008 15.375 12.3777 15.375 12.875V21.625C14.8346 21.2183 14.1763 20.9989 13.5 21ZM23.5 21H18.5C17.8237 20.9989 17.1654 21.2183 16.625 21.625V12.875C16.625 12.3777 16.8225 11.9008 17.1742 11.5492C17.5258 11.1975 18.0027 11 18.5 11H23.5V21Z"
                fill="#18181B"
              />
            </svg>

            <div className="flex items-end gap-2">
              <FigmaTypography
                data-testid="project-title"
                variant="heading-3"
                className="max-w-[17vw] font-medium text-ellipsis overflow-hidden whitespace-nowrap"
              >
                Instructions
              </FigmaTypography>
            </div>
            <AppButton
              data-testid="instructions-header-export-button"
              appearance="text"
              title="Export"
              color="zinc100"
              size="icon"
              startAdornment={<Download />}
              onClick={handleExport}
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {isLoading && (
              <div className="flex items-center gap-1.5 text-innoveraZinc-500 text-[14px]">
                <Spinner aria-label={t("loading")} className="size-4" />
                Saving...
              </div>
            )}
            <AppButton
              data-testid="instructions-header-close-button"
              appearance="text"
              size="icon"
              title="Close"
              onClick={handleClose}
              startAdornment={<XIcon className="size-5" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
